import type { NoteName, NoteWithOctave, Scale, StandardNoteNameWithOctave } from '@tunasong/schemas'
import type { Chord, ChordVariant } from '../chord/chord-types.js'
import { getName } from '../chord/get-name.js'
import {
  getNoteByChromaticOffset,
  getNoteMIDINumber,
  getNoteOctaveByChromaticOffset,
  getPrevChromaticNotes,
  NoteLib,
  sortFn,
  uniqueFn,
  type NoteDegree,
} from '../note/index.js'
import { ScaleLib } from '../scale/index.js'
import type { ChordShape } from './chord-shapes.js'
import { CHORD_SHAPES, MUTED_STRING, OPEN_STRING } from './chord-shapes.js'
import type { Tuning } from './tuning.js'
import { GuitarTunings } from './tuning.js'
import type { Articulation } from './articulation.js'

export type Fret = number | typeof OPEN_STRING | typeof MUTED_STRING
export type StringNumber = 1 | 2 | 3 | 4 | 5 | 6

export const getFingeringFromFrets = (frets: number[]) =>
  frets.map((f, idx) => ({ fret: f, str: idx + 1 })) as Fingering[]
/**
 * Return an array of fretted strings, where index 0 is string 1.
 * 0 is open string, -1 is muted string.
 */
export const getFretsFromFingering = (fingerings: Fingering[]) => {
  /** Start with all open strings */
  const frets: Fret[] = [0, 0, 0, 0, 0, 0]
  for (const { str, fret } of fingerings) {
    frets[str - 1] = fret
  }
  return frets
}

/** T is thumb - @see https://www.garnerguitar.com/courses/beginner/course1/finger-names */
export type FingerNumber = 'T' | 1 | 2 | 3 | 4 /* right */ | 'P' | 'I' | 'M' | 'A' /* left */

export interface Fingering {
  /**
   * From 1 = high E, to 6 = low E.
   */
  str: StringNumber
  /** The fret for the fingering.
   *  null: Muted
   *   0: Open string
   * >=1: Fretted at fret
   */
  fret: Fret

  articulation?: Articulation

  /** A parenthesised fret, `(3)` — sounded but not struck. */
  ghost?: boolean

  // The finger number
  finger?: FingerNumber
}

export type FingeringWithMetadata = Fingering & {
  note: NoteName
} & (
    | { inScale: true; scaleDegree: NoteDegree }
    | {
        inScale: false | undefined | null
        scaleDegree: null | undefined
      }
  )

export function isFingeringWithMetadata(f: Fingering | FingeringWithMetadata): f is FingeringWithMetadata {
  return 'note' in f && 'inScale' in f && 'fret' in f && 'str' in f
}

export const getFingeringWithMetadata = (fingering: Fingering[], scale?: Scale | null): FingeringWithMetadata[] =>
  fingering.map(f => {
    const note = getNoteFromFingering(f)
    const inScale = scale ? ScaleLib.noteInScale(scale, note) : undefined
    return {
      ...f,
      note,
      inScale,
      scaleDegree: inScale && scale ? ScaleLib.getInterval(scale, note) : undefined,
    } as FingeringWithMetadata
  })

export const removeMetadata = (fingering: Fingering[] | FingeringWithMetadata[]): Fingering[] =>
  fingering.map(f => ({
    str: f.str,
    fret: f.fret,
  }))

/** Get the note */
export const getNoteFromFingering = ({ str, fret }: Fingering, tuning = GuitarTunings.normal): NoteName | null => {
  const stringNote = tuning[str]
  if (fret === MUTED_STRING || !stringNote) {
    return null
  }
  const { note } = NoteLib.getNoteAndOctave(stringNote)

  return getNoteByChromaticOffset(note, fret)
}

export const getNoteOctaveFromFingering = (
  { str, fret }: Fingering,
  tuning = GuitarTunings.normal
): StandardNoteNameWithOctave | null => {
  if (fret === MUTED_STRING) {
    return null
  }
  const note = tuning[str]

  return getNoteOctaveByChromaticOffset(note, fret)
}

export const getNotesFromFingering = (fingering: Fingering[]): NoteName[] =>
  fingering
    .map(f => getNoteFromFingering(f) as NoteName)
    .filter(Boolean)
    .filter(uniqueFn)
    .sort(sortFn)

/** @todo implement properly */
export const getFingering = (chord?: ChordVariant): Fingering[] | undefined => {
  if (!chord) {
    return
  }
  let c: Chord = { ...chord }
  for (let fret = 0; fret < 12; fret++) {
    c = fret === 0 ? c : { root: getPrevChromaticNotes(c.root)[0], variant: c.variant }
    const name = getName(c) as ChordShape
    /** Do we have a shape for the chord? */
    const shape = CHORD_SHAPES[name]
    if (shape) {
      return barre(fret, name)
    }
  }
}

export const transpose = ({
  fingerings,
  steps = 1,
  allowTruncateSteps = 0,
}: {
  fingerings: Fingering[]
  steps?: number
  allowTruncateSteps?: number
}) => {
  const transposedFingerings = fingerings.map(f => ({
    ...f,
    fret: f.fret !== null && f.fret >= 0 ? f.fret + steps : f.fret,
  }))
  // Wrap-around if we have negative fret numbers
  const minFret = Math.min(...transposedFingerings.map(f => f.fret).filter(f => f !== MUTED_STRING))
  return minFret < allowTruncateSteps
    ? transposedFingerings.map(f => ({ ...f, fret: f.fret !== null ? f.fret + 12 : null }))
    : transposedFingerings.filter(f => (typeof f.fret === 'number' ? f.fret >= 0 : true))
}

export const barre = (fret: Exclude<Fret, null>, shape: ChordShape): Fingering[] => {
  const fingering = CHORD_SHAPES[shape] as Fingering[]
  if (!fingering) {
    return []
  }
  if (fret === 0) {
    return fingering
  }
  /** Transpose the chord */
  return transpose({ fingerings: fingering, steps: fret })
}

export interface BarreRange {
  barre: number
  startString: number
  endString: number
}

/** Get the string range for a barre, or null if no barre is possible */
export const getBarreRange = (fingerings: Fingering[]) => {
  const hasOpenStrings = fingerings.filter(f => f.fret === OPEN_STRING).length > 0
  if (hasOpenStrings) {
    return null
  }
  /** Barre is the first fret of the fingerings and remove the muted and open string */
  const sorted = fingerings
    .filter(Boolean)
    .sort((a, b) => (a.fret === b.fret ? 0 : a.fret && b.fret && a.fret < b.fret ? -1 : 1))
  const fingering = sorted[0]
  if (!fingering) {
    return null
  }
  const { fret } = fingering
  const candidates = sorted.filter(f => f.fret === fret)
  if (candidates.length < 2) {
    return null
  }
  /** Find start and end fret */
  const strings = candidates.map(c => c.str)
  const startString = Math.min.apply(null, strings)
  const endString = Math.max.apply(null, strings)
  const range: BarreRange = {
    barre: fret as number, // We have filtered out null and open strings
    startString,
    endString,
  }
  return range
}

/** Get the note ranges for a tuning */
export const getFretboardRange = (tuning = GuitarTunings.normal, frets = 20) => {
  const range: Record<
    number,
    { from: { note: NoteWithOctave; midiNumber: number }; to: { note: NoteWithOctave; midiNumber: number } }
  > = {}
  for (const [fret, note] of Object.entries(tuning)) {
    const toNote = getNoteOctaveByChromaticOffset(note, frets)
    const from = NoteLib.getNoteAndOctave(note)
    const fromMidi = getNoteMIDINumber(note)
    const to = NoteLib.getNoteAndOctave(toNote)
    const toMidi = getNoteMIDINumber(toNote)
    range[Number(fret)] = { from: { note: from, midiNumber: fromMidi }, to: { note: to, midiNumber: toMidi } }
  }
  return range
}

/** Get fingering from Notes */
export const getFingeringFromNotes = ({
  notes,
  tuning = GuitarTunings.normal,
  frets = 20,
}: {
  notes: StandardNoteNameWithOctave[]
  tuning: Tuning
  capo?: number
  frets?: number
}): Fingering[] => {
  // Make a map of the tuning and the note ranges, i.e., the starting note/octave and the ending note/octave
  const range = getFretboardRange(tuning, frets)

  const fingering: Fingering[] = []

  // For each string, find fingerings that match the note(s)
  for (const [str, noteWithOctave] of Object.entries(range)) {
    const strNum = Number(str) as StringNumber

    for (const candidateNote of notes) {
      const midiNumber = getNoteMIDINumber(candidateNote)

      const inRange = midiNumber >= noteWithOctave.from.midiNumber && midiNumber <= noteWithOctave.to.midiNumber
      if (!inRange) {
        continue
      }
      // The note is in range of the string, find the fingering
      const fret = midiNumber - noteWithOctave.from.midiNumber
      fingering.push({ str: strNum, fret })
    }
  }
  return fingering
}

// Helper function to generate fingerings on the fretboard for a scale
export const getFingeringsForScale = ({
  scale,
  startFret = 0,
  endFret = 12,
}: {
  scale: Scale
  startFret?: number
  endFret?: number
}): FingeringWithMetadata[] => {
  const fingerings: FingeringWithMetadata[] = []

  // For each string and fret within our display range
  for (let str = 1; str <= 6; str++) {
    for (let fret = startFret; fret <= endFret; fret++) {
      const fingering = { str, fret } as Fingering
      const note = getNoteFromFingering(fingering)

      const inScale = ScaleLib.noteInScale(scale, note)
      if (note && inScale) {
        const degree = ScaleLib.getInterval(scale, note)
        // This note is in the scale, add it to our fingerings
        fingerings.push({
          ...fingering,
          note,
          inScale: true,
          scaleDegree: degree ? degree : undefined,
        } as FingeringWithMetadata)
      }
    }
  }

  return fingerings
}

export const getFirstFingerPosition = (fingerings: Fingering[]) => {
  const stringFretMap = new Map<number, number>()
  /** We chose the firstFingerFret as the rounded average of the minimum fret value for each string */
  for (const fingering of fingerings) {
    const { str, fret } = fingering
    const currentMinFret = stringFretMap.get(str)
    const newVal = currentMinFret ? Math.min(currentMinFret, fret ?? currentMinFret) : fret
    if (newVal !== OPEN_STRING && newVal !== MUTED_STRING) {
      stringFretMap.set(str, newVal)
    }
  }
  const firstFingerFretMean = Array.from(stringFretMap.values()).reduce((a, b) => a + b, 0) / stringFretMap.size
  const firstFingerFret = Math.round(firstFingerFretMean)

  // Find the fractional part of the average
  const fractionalPart = firstFingerFretMean % 1

  // The default behavior is to round to the nearest unless the fractional part is around 0.5 when we choose the lowest string instead
  const lambda = 0.05
  if (fractionalPart > 0.5 - lambda && fractionalPart < 0.5 + lambda) {
    // Find the string with the lowest fret
    const lowestString = Math.max(...Array.from(stringFretMap.keys()))
    const lowestFret = stringFretMap.get(lowestString) ?? firstFingerFret
    return lowestFret
  }

  return firstFingerFret
}

/**
 * Adds finger number information to each fingering in the provided array, based on fret positions and a maximum finger range.
 *
 * <p>
 * For each string in the given fingerings, this function determines the optimal starting fret position (usually the lowest fret or a default position)
 * and assigns finger numbers (1-4) to each note on that string, ensuring that the assigned fingers do not span more than {@link maxRange} frets.
 * If the range of frets on a string exceeds {@link maxRange}, the starting fret is adjusted so that the highest note fits within the allowed range.
 * </p>
 *
 * @param fingerings - Array of {@link Fingering} objects representing notes to be fingered.
 * @param maxRange - The maximum number of frets that can be covered by four fingers on a string (default is 4).
 * @returns A new array of {@link Fingering} objects, each with an added {@code finger} property indicating the assigned finger number.
 *
 * @remarks
 * - The function assumes standard guitar fingering, where each finger covers one fret.
 * - The {@code finger} property is calculated as {@code (fret - startFret + 1)}.
 * - Only fingerings within the adjusted range are included in the result.
 *
 * @see Fingering
 * @see getFirstFingerPosition
 */
export const addFingerInfo = <T extends Fingering>(
  fingerings: T[],
  maxRange = 4
): (T & { finger?: FingerNumber })[] => {
  const defaultFirstFingerPosition = getFirstFingerPosition(fingerings)

  // Four fingers, max range per string is 4 frets

  const fingeringsWithFinger: T[] = []

  const minString = Math.min(...fingerings.map(f => f.str))
  const maxString = Math.max(...fingerings.map(f => f.str))
  for (let str = minString; str <= maxString; str++) {
    const fingeringsOnString = fingerings.filter(f => f.str === str)
    // Filter out muted and open string (null and 0)
    const fretsOnString = fingeringsOnString.map(f => f.fret).filter(Boolean)
    const startFret = Math.min(...fretsOnString, defaultFirstFingerPosition)

    fingeringsWithFinger.push(
      ...fingeringsOnString
        .map(f => {
          const finger = f.fret ? f.fret - startFret + 1 : undefined
          // We cannot finger this, so we'll drop the note
          if (finger && (finger <= 0 || finger > maxRange)) {
            return undefined
          }
          return {
            ...f,
            finger: finger as FingerNumber,
          }
        })
        .filter(Boolean)
        .filter(f => f.fret !== null)
    )
  }
  return fingeringsWithFinger
}
